import React, { useEffect, useState } from "react";
import { Button, Divider, makeStyles } from "@material-ui/core";
import moment from "moment";
import _ from "lodash";

import BasicInformationForm from "./BasicInformationForm";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import BasicInformationSummary from "./BasicInformationForm/summary";
import { getUserPreference } from "../../../utilities/common";
import InProgressForm from "./InProgressForm";
import * as editOrderAPI from "../../../actions/editOrderActions";
import { useAlert } from "../../../utilities/hooks";
import { alertType } from "../../Common/UniAlert/context";
import {
  closeInvestigation,
  createNewInvestigation,
  updateInprogressInvestigation,
  updateNewInvestigation
} from "../../../actions/incidentManagementActions";
import Validation, { FormValidation } from "../../../utilities/formValidation";
import { updateIncidentFormInvestigationId } from "../../../reducers/uiSlice";
import InProgressSummary from "./InProgressForm/summary";
import ReviewForm from "./ReviewForm";
import ReviewSummary from "./ReviewForm/summary";
import SectionExpand from "./SectionExpand";

const initialBasicFormState = {
  level: "",
  status: 1,
  record_time: moment().unix(),
  tracking_no: "",
  occurrence_time: moment().unix(),
  parcel_last_state: null,
  location: null,
  recorded_by: null,
  source: "",
  other_source: "",
  incident_type: "",
  detail: "",
  action_required_type: "",
  action_required_detail: "",
}

const initialInProgressFormState = {
  handler: null,
  caused_by: "",
  caused_by_detail: "",
  root_cause: "",
  root_cause_detail: "",
  result_type: "",
  result_detail: "",
  corrective_action: null,
  corrective_action_detail: "",
}

const initialReviewFormState = {
  close_time: moment().unix(),
  review_type: "",
  consignee_refund: "",
  client_refund: "",
}

const Steps = {
  CreateBasicInfo: 1,
  EditBasicInfo: 2,
  StartProgress: 3,
  InProgress: 4,
  EditInProgress: 5,
  InProgressSummary: 6,
  Review: 7,
  Closed: 8
}

const useStyles = makeStyles((theme) => ({
  section: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    margin: '0px !important'
  },
  bottomSubmit: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  }
}))

function EditIncidentForm(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const alertDialog = useAlert();
  const { t } = useTranslation('incidentManagement');
  const userPref = getUserPreference();

  const { investigationId, formValueUpdated, trackingNumber, incident } = props;
  const [basicInformation, setBasicInformation] = useState({ ...initialBasicFormState, recorded_by: userPref.userId, tracking_no: trackingNumber });
  const [inProgressInformation, setInProgressInformation] = useState({ ...initialInProgressFormState, handler: userPref.userId });
  const [reviewInformation, setReviewInformation] = useState(initialReviewFormState);
  const [notes, setNotes] = useState("");

  const [currentStep, setCurrentStep] = useState(Steps.CreateBasicInfo);
  const [orderInfoLoading, setOrderInfoLoading] = useState(false);

  const statusList = useSelector(state => state.incidentManagement.statusList);
  const branchList = useSelector(state => state.common.branches);
  const userList = useSelector(state => state.common.users);

  const [clientRefund, setClientRefund] = useState(0);
  const [consigneeRefund, setConsigneeRefund] = useState(0);

  const handleFormChange = (event, stateName, setState) => {
    const { name, type, value: rawValue } = event.target;
    let value = rawValue;

    if (type === "date") {
      value = moment(rawValue).unix();
    }

    switch (name) {
      case "notes":
        handleNotesChange(value);
        break;
      case "consignee_refund":
        handleConsigneeRefundChange(value);
        break;
      case "client_refund":
        handleClientRefundChange(value);
        break;
      default:
        if (name) {
          setState(prevState => ({ ...prevState, [name]: value }));
        }
        break;
    }
  };


  const handleClientRefundChange = (text,) => {
    setClientRefund(text);
  }

  const handleConsigneeRefundChange = (text,) => {
    setConsigneeRefund(text);
  }

  const handleNotesChange = (text) => {
    setNotes(text);
  }

  const SubmitButton = (props) => {
    const { ...rest } = props;
    const getButtonTitle = () => {
      switch (currentStep) {
        case Steps.CreateBasicInfo:
          return 'create_investigation';
        case Steps.EditBasicInfo:
        case Steps.InProgress:
        case Steps.EditInProgress:
          return 'save_investigation';
        case Steps.StartProgress:
          return 'start_investigation';
        case Steps.InProgressSummary:
          return 'review_investigation';
        case Steps.Review:
          return 'close_investigation';
        case Steps.Closed:
        default:
          break;
      }
    }

    return (
      <React.Fragment>
        <Button
          color='primary'
          variant='contained'
          {...rest}
        >
          {t(getButtonTitle())}
        </Button>
      </React.Fragment>
    )
  }

  const onNextStep = async () => {
    try {
      switch (currentStep) {
        case Steps.CreateBasicInfo:
        case Steps.EditBasicInfo:
          await validateAndUpdateIncident();
          setCurrentStep(Steps.StartProgress);
          break;
        case Steps.StartProgress:
          setCurrentStep(Steps.InProgress);
          break;
        case Steps.InProgress:
        case Steps.EditInProgress:
          await validateAndUpdateIncident();
          setCurrentStep(Steps.InProgressSummary);
          break;
        case Steps.InProgressSummary:
          await validateAndUpdateIncident();
          setCurrentStep(Steps.Review);
          break;
        case Steps.Review:
          await validateAndUpdateIncident();
          setCurrentStep(Steps.Closed);
          break;
        default:
          break;
      }
    } catch (error) {
      if (error.name === "FormValidationError") {
        let errorMessages = [];
        _.forEach(error.errors, (err) => {
          errorMessages.push(`${t(err.fieldName)} ${t(`common:${err.message}`)}`)
        })
        let formError = [
          t('dialog:please_fill_out_sections'),
          ...errorMessages
        ]
        error.message = formError;
      }
      alertDialog.addAlertDialog(
        t('incident_management'),
        error.message
      )
      alertDialog.showAlert()
    }
  }

  const getOrderInfo = (tno) => {
    return new Promise((resolve) => {
      setOrderInfoLoading(true);

      dispatch(editOrderAPI.getOrderInfo(tno, 'tno'))
        .then(data => {
          resolve({
            tracking_no: tno,
            parcel_last_state: data.tracking.state,
            driver_id: data.orders.shipping_staff_id,
            location: data.tracking.last_operation_warehouse,
            occurrence_time: data.tracking.incident_occurence_time
          })
        }).catch(error => {
          alertDialog.addAlertDialog(
            t('incident_management'),
            t('dialog:cannot_find_order_message', { tracking_number: tno }),
            alertType.ERROR
          );
          alertDialog.showAlert();
          resolve({
            tracking_no: "",
            parcel_last_state: null,
            location: null,
            occurrence_time: null
          })
        }).finally(() => {
          setOrderInfoLoading(false);
        })
    })
  }

  const validateAndUpdateIncident = async () => {
    try {
      if (currentStep === Steps.CreateBasicInfo || currentStep === Steps.EditBasicInfo) {
        let trimmedForm = trimBasicForm(basicInformation);
        trimmedForm['notes'] = notes;
        validateBasicForm(trimmedForm)
        if (currentStep === Steps.EditBasicInfo && investigationId) {
          //delete these fields since they cannot be updated
          delete trimmedForm['incident_date'];
          delete trimmedForm['recorded_by'];
          await dispatch(updateNewInvestigation(investigationId, trimmedForm))
        } else {
          let investigationId = await dispatch(createNewInvestigation(trimmedForm))
          dispatch(updateIncidentFormInvestigationId(investigationId))
        }
      } else if (currentStep === Steps.InProgress || currentStep === Steps.EditInProgress) {
        inProgressInformation["notes"] = notes;
        validateInProgressForm(inProgressInformation)
        await dispatch(updateInprogressInvestigation(investigationId, inProgressInformation))
      } else if (currentStep === Steps.Review) {
        reviewInformation['notes'] = notes
        reviewInformation['client_refund'] = clientRefund
        reviewInformation['consignee_refund'] = consigneeRefund
        validateReviewForm(reviewInformation)
        await dispatch(closeInvestigation(investigationId, reviewInformation))
      } else if (currentStep === Steps.InProgressSummary) {
        ValidateInprogressSummary(inProgressInformation)
      }
      formValueUpdated()
    } catch (error) {
      throw error
    }
  }

  const validateBasicForm = (formData) => {
    let formValidation = new FormValidation()

    formValidation.addValidation(new Validation(formData.tracking_no, "tracking_number").isRequired())
    formValidation.addValidation(new Validation(formData.level, "incident_level").isRequired())
    formValidation.addValidation(new Validation(formData.recorded_by, "recorded_by").isRequired())
    formValidation.addValidation(new Validation(formData.source, "start_from").isRequired())
    formValidation.addValidation(new Validation(formData.incident_type, "incident_type").isRequired())


    if (isStatusRequireInput(statusList.sourceList, formData.source))
      formValidation.addValidation(new Validation(formData.other_source, "other_source").isRequired().maxChar())

    if (isStatusRequireInput(statusList.incidentTypeList, formData.incident_type))
      formValidation.addValidation(new Validation(formData.detail, "detail").isRequired().maxChar())

    try {
      return formValidation.validate()
    } catch (error) {
      throw error
    }
  }

  const validateInProgressForm = (formData) => {
    let formValidation = new FormValidation()

    formValidation.addValidation(new Validation(formData.handler, "handler").isRequired())
    formValidation.addValidation(new Validation(formData.caused_by, "caused_by").isRequired())
    formValidation.addValidation(new Validation(formData.root_cause, "root_cause").isRequired())

    if (isStatusRequireInput(statusList.causedByList, formData.caused_by))
      formValidation.addValidation(new Validation(formData.caused_by_detail, "parcel_detail").isRequired().maxChar())

    if (isStatusRequireInput(statusList.rootCauseList, formData.root_cause))
      formValidation.addValidation(new Validation(formData.root_cause_detail, "root_cause_detail").isRequired().maxChar())

    if (isStatusRequireInput(statusList.resultList, formData.result_type))
      formValidation.addValidation(new Validation(formData.result_detail, "result_detail").isRequired().maxChar())


    if (isStatusRequireInput(statusList.correctiveActionList, formData.corrective_action))
      formValidation.addValidation(new Validation(formData.corrective_action_detail, "corrective_action_detail").isRequired().maxChar())

    try {
      return formValidation.validate()
    } catch (error) {
      throw error
    }
  }

  const validateReviewForm = (formData) => {
    let formValidation = new FormValidation()
    formValidation.addValidation(new Validation(formData.review_type, "review_type").isRequired())

    try {
      return formValidation.validate()
    } catch (error) {
      throw error
    }
  }

  const ValidateInprogressSummary = (formData, preData) => {
    let formValidation = new FormValidation()
    formValidation.addValidation(new Validation(formData.corrective_action, "corrective_action_record").isRequired())
    try {
      return formValidation.validate()
    } catch (error) {
      throw error
    }
  }

  const trimBasicForm = (form) => {
    let allowedList = [
      'level',
      'recorded_by',
      'incident_date',
      'tracking_no',
      'occurrence_time',
      'source',
      'other_source',
      'incident_type',
      'detail',
    ]
    return _.pick(form, allowedList)
  }

  const isStatusRequireInput = (list, id) => {
    return _.find(list, { 'id': id })?.input || false
  }

  const updateIncidentForm = (oldObj, newObj) => {
    let result = oldObj
    _.forEach(_.keys(newObj), (key) => {
      if (newObj[key] !== null && oldObj.hasOwnProperty(key) && key !== "notes") {
        result = { ...result, [key]: newObj[key] }
      }
    })
    handleNotesChange(newObj?.notes || "");
    return result
  }

  const handleTrackingNumberChange = async (tno) => {
    let orderInfo = await getOrderInfo(tno);
    setBasicInformation({ ...basicInformation, ...orderInfo })
  }

  useEffect(() => {
    //Edit incident
    (async () => {
      if (investigationId !== null) {
        if (incident) {
          let newBasicInfo = updateIncidentForm(basicInformation, incident)
          setBasicInformation(newBasicInfo)
          let newInProgressInfo = updateIncidentForm(inProgressInformation, incident)
          setInProgressInformation(newInProgressInfo)
          let newReviewInfo = updateIncidentForm(reviewInformation, incident)
          setReviewInformation(newReviewInfo)
          if (incident.status === 1) {
            setCurrentStep(Steps.StartProgress)
          } else if (incident.status === 2) {
            setCurrentStep(Steps.InProgressSummary)
          } else if (incident.status === 3) {
            setCurrentStep(Steps.Closed)
          }
          //Do this last
          if (!newBasicInfo.parcel_last_state) {
            let orderInfo = await getOrderInfo(newBasicInfo.tracking_no)
            newBasicInfo = { ...newBasicInfo, ...orderInfo }
            setBasicInformation(newBasicInfo)
          }
        }
      }
    })();
  }, [investigationId])

  return (
    <React.Fragment>
      {currentStep === Steps.CreateBasicInfo || currentStep === Steps.EditBasicInfo ?
        <BasicInformationForm
          data={{ ...basicInformation, notes }}
          branchList={branchList}
          userList={userList}
          statusList={statusList}
          onTrackingNumberChange={handleTrackingNumberChange}
          onFormChange={(event) => handleFormChange(event, basicInformation, setBasicInformation)}
        /> : null
      }
      {currentStep !== Steps.CreateBasicInfo && currentStep !== Steps.EditBasicInfo ?
        <SectionExpand
          aria-controls="basic-info-content"
          id="basic-info-header"
          title={t('basic_information')}
          editable={currentStep === Steps.StartProgress ? true : false}
          onEditClick={() => setCurrentStep(Steps.EditBasicInfo)}
        >
          <BasicInformationSummary
            data={{ ...basicInformation, notes }}
            branchList={branchList}
            userList={userList}
            statusList={statusList}
            orderInfoLoading={orderInfoLoading}
            isCurrentStep={currentStep === Steps.StartProgress ? true : false}
          />
        </SectionExpand>
        : null
      }
      {currentStep === Steps.InProgress || currentStep === Steps.EditInProgress ?
        <React.Fragment>
          <Divider />
          <InProgressForm
            className={classes.section}
            data={{ ...inProgressInformation, notes }}
            userList={userList}
            statusList={statusList}
            onFormChange={(event) => handleFormChange(event, inProgressInformation, setInProgressInformation)}
          />
        </React.Fragment> : null
      }
      {currentStep === Steps.InProgressSummary || currentStep === Steps.Review || currentStep === Steps.Closed ?
        <React.Fragment>
          <Divider />
          <SectionExpand
            className={classes.section}
            aria-controls="inprogress-info-content"
            id="inprogress-info-header"
            title={t('in_progress')}
            editable={currentStep === Steps.InProgressSummary ? true : false}
            onEditClick={() => setCurrentStep(Steps.EditInProgress)}
          >
            <InProgressSummary
              data={{ ...inProgressInformation, notes }}
              userList={userList}
              statusList={statusList}
              isCurrentStep={currentStep === Steps.InProgressSummary ? true : false}
            />
          </SectionExpand>
        </React.Fragment> : null
      }
      {currentStep === Steps.Review ?
        <React.Fragment>
          <Divider />
          <ReviewForm
            className={classes.section}
            data={{ ...reviewInformation, notes, consigneeRefund, clientRefund }}
            statusList={statusList}
            onFormChange={(event) => handleFormChange(event, reviewInformation, setReviewInformation)}
          />
        </React.Fragment> : null
      }
      {currentStep === Steps.Closed ?
        <React.Fragment>
          <Divider />
          <SectionExpand
            className={classes.section}
            aria-controls="review-info-content"
            id="review-info-header"
            title={t('review')}
            editable={currentStep === Steps.Closed ? true : false}
          >
            <ReviewSummary
              data={{ ...reviewInformation, notes, consigneeRefund, clientRefund }}
              statusList={statusList}
            />
          </SectionExpand>
        </React.Fragment> : null
      }
      {currentStep !== Steps.Closed ?
        <div className={classes.bottomSubmit}>
          <SubmitButton
            onClick={onNextStep}
          />
        </div> : null
      }
    </React.Fragment>
  )
}

export default EditIncidentForm;

export const getNameFromList = (list, id) => {
  let item = _.find(list, { 'id': id });
  return item?.name || item?.username || " N/A"
}